/**
 * GraphQL Projects Repository
 * Handles all GraphQL operations for Projects, Tasks, Time Entries, Milestones
 *
 * Uses Martha Backend (database.teknology.eu) as data source
 */

import {
  graphqlQuery,
  graphqlMutation,
  PROJECT_FIELDS,
  TASK_FIELDS,
  TIME_ENTRY_FIELDS,
  MILESTONE_FIELDS,
} from '../database/graphqlClient';
import type {
  Project,
  Task,
  TimeEntry,
  Milestone,
  ProjectStatus,
  TaskStatus,
  TeamMember,
} from '../domain/models';

// =============================================================================
// GRAPHQL QUERIES
// =============================================================================

const QUERIES = {
  projects: `
    query ListProjects {
      projects {
        ${PROJECT_FIELDS}
      }
    }
  `,

  project: `
    query GetProject($id: ID!) {
      project(id: $id) {
        ${PROJECT_FIELDS}
      }
    }
  `,

  tasks: `
    query ListTasks($projectId: ID) {
      tasks(projectId: $projectId) {
        ${TASK_FIELDS}
      }
    }
  `,

  task: `
    query GetTask($id: ID!) {
      task(id: $id) {
        ${TASK_FIELDS}
      }
    }
  `,

  timeEntries: `
    query ListTimeEntries($projectId: ID) {
      timeEntries(projectId: $projectId) {
        ${TIME_ENTRY_FIELDS}
      }
    }
  `,

  milestones: `
    query ListMilestones($projectId: ID) {
      milestones(projectId: $projectId) {
        ${MILESTONE_FIELDS}
      }
    }
  `,
};

// =============================================================================
// GRAPHQL MUTATIONS
// =============================================================================

const MUTATIONS = {
  createProject: `
    mutation CreateProject($input: ProjectInput!) {
      createProject(input: $input) {
        ${PROJECT_FIELDS}
      }
    }
  `,

  updateProject: `
    mutation UpdateProject($id: ID!, $input: ProjectInput!) {
      updateProject(id: $id, input: $input) {
        ${PROJECT_FIELDS}
      }
    }
  `,

  deleteProject: `
    mutation DeleteProject($id: ID!) {
      deleteProject(id: $id)
    }
  `,

  createTask: `
    mutation CreateTask($input: TaskInput!) {
      createTask(input: $input) {
        ${TASK_FIELDS}
      }
    }
  `,

  updateTask: `
    mutation UpdateTask($id: ID!, $input: TaskInput!) {
      updateTask(id: $id, input: $input) {
        ${TASK_FIELDS}
      }
    }
  `,

  deleteTask: `
    mutation DeleteTask($id: ID!) {
      deleteTask(id: $id)
    }
  `,

  createTimeEntry: `
    mutation CreateTimeEntry($input: TimeEntryInput!) {
      createTimeEntry(input: $input) {
        ${TIME_ENTRY_FIELDS}
      }
    }
  `,

  createMilestone: `
    mutation CreateMilestone($input: MilestoneInput!) {
      createMilestone(input: $input) {
        ${MILESTONE_FIELDS}
      }
    }
  `,

  updateMilestone: `
    mutation UpdateMilestone($id: ID!, $input: MilestoneInput!) {
      updateMilestone(id: $id, input: $input) {
        ${MILESTONE_FIELDS}
      }
    }
  `,

  deleteMilestone: `
    mutation DeleteMilestone($id: ID!) {
      deleteMilestone(id: $id)
    }
  `,
};

// =============================================================================
// RESPONSE TYPES
// =============================================================================

interface ProjectsQueryResult {
  projects: GraphQLProject[];
}

interface ProjectQueryResult {
  project: GraphQLProject | null;
}

interface TasksQueryResult {
  tasks: GraphQLTask[];
}

interface TaskQueryResult {
  task: GraphQLTask | null;
}

interface TimeEntriesQueryResult {
  timeEntries: GraphQLTimeEntry[];
}

interface MilestonesQueryResult {
  milestones: GraphQLMilestone[];
}

interface CreateProjectResult {
  createProject: GraphQLProject;
}

interface UpdateProjectResult {
  updateProject: GraphQLProject;
}

interface CreateTaskResult {
  createTask: GraphQLTask;
}

interface UpdateTaskResult {
  updateTask: GraphQLTask;
}

interface CreateTimeEntryResult {
  createTimeEntry: GraphQLTimeEntry;
}

interface CreateMilestoneResult {
  createMilestone: GraphQLMilestone;
}

interface UpdateMilestoneResult {
  updateMilestone: GraphQLMilestone;
}

// =============================================================================
// GRAPHQL TYPES (as returned from API)
// =============================================================================

interface GraphQLProject {
  id: string;
  title: string;
  description: string;
  status: string;
  health: string | null;
  priority: string;
  progress: number;
  startDate: string;
  dueAt: string | null;
  budgetTotal: number | null;
  budgetUsed: number | null;
  risk: string;
  favorite: boolean;
  clientId: string | null;
  clientName: string | null;
  /** The backend stores the team as a list of user ids. */
  team: string[] | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface GraphQLTask {
  id: string;
  projectId: string;
  parentId: string | null;
  order: number;
  title: string;
  description: string | null;
  status: string;
  done: boolean;
  priority: string | null;
  startAt: string | null;
  dueAt: string | null;
  estimatedMinutes: number | null;
  loggedMinutes: number | null;
  assignees: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface GraphQLTimeEntry {
  id: string;
  projectId: string;
  taskId: string | null;
  userId: string;
  userName: string | null;
  minutes: number;
  costRate: number;
  billableRate: number;
  billable: boolean;
  description: string | null;
  at: string;
  createdAt: string;
}

interface GraphQLMilestone {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  dueDate: string;
  completed: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// TEAM MAPPING (backend: user ids, frontend: TeamMember objects)
// =============================================================================

const DEFAULT_TEAM_ROLE = 'member';

function toTeamIds(team: Project['team'] | undefined): string[] {
  return (team ?? []).map(member => member.userId).filter(Boolean);
}

/**
 * Build a URL-safe, deterministic slug from a project title.
 * Used as a fallback id when the backend returns `id: ""` (see
 * TODO(issue:backend-id-serialization) in mapProjectFromGraphQL).
 * Same title → same slug → stable deep links across reloads.
 */
function slugifyTitle(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'project';
}

// =============================================================================
// REAL-ID DISCOVERY (workaround for backend `id: ""` on Project reads)
//
// The `database.teknology.eu` resolvers currently return `id: ""` on the
// `Project` type for every read path (listProjects, project(id: ...)),
// while the `Task` / `Milestone` / `TimeEntry` foreign keys still carry
// the real uuid (`projectId: "16aa44e0-..."`). Without help, sub-queries
// cannot resolve a project and the detail view renders empty.
//
// Strategy: ask the backend for the foreign keys we already trust
// (tasks.projectId), then ask `project(id: <real>)` to recover the
// project title for each uuid and match it back to the listProjects
// rows. Once the title→uuid map is built (one-shot, module-level
// cache), mapProjectFromGraphQL attaches the real uuid as `realId`
// and sub-queries prefer it. `id` stays the synth- slug so URLs and
// React Query keys remain stable when the discovery hasn't run yet.
// =============================================================================

const DISCOVERY_TASKS_QUERY = `
  query DiscoveryTasks {
    tasks { projectId }
  }
`;

const DISCOVERY_PROJECT_TITLE_QUERY = `
  query DiscoveryProjectTitle($id: ID!) {
    project(id: $id) { title }
  }
`;

interface DiscoveryTasksResult {
  tasks: Array<{ projectId: string | null }>;
}

interface DiscoveryProjectResult {
  project: { title: string } | null;
}

let realIdByTitlePromise: Promise<Map<string, string>> | null = null;

/**
 * Build a `title -> realUuid` map by:
 *  1. Fetching all `tasks.projectId` values (1 cheap query, the FKs are
 *     real even when the Project read id is empty).
 *  2. For each distinct uuid, calling `project(id: <uuid>)` to recover
 *     its title (the resolver returns the correct title even though it
 *     also returns `id: ""`).
 *  3. Matching by title to the listProjects rows later.
 *
 * The result is cached module-level so the work is paid at most once
 * per app session. Failure of any step is non-fatal: the caller falls
 * back to the synth- slug and sub-queries stay empty for projects we
 * could not resolve (known limitation, removed when the backend ships
 * real ids on Project reads).
 */
async function buildRealIdByTitle(): Promise<Map<string, string>> {
  if (realIdByTitlePromise) return realIdByTitlePromise;
  realIdByTitlePromise = (async () => {
    try {
      const tasksResult = await graphqlQuery<DiscoveryTasksResult>(DISCOVERY_TASKS_QUERY);
      const uuids = [
        ...new Set(
          (tasksResult.tasks ?? [])
            .map((t) => t.projectId)
            .filter((u): u is string => !!u)
        ),
      ];
      const map = new Map<string, string>();
      // Resolve titles in parallel — each is a tiny lookup.
      const titleLookups = await Promise.all(
        uuids.map(async (uuid) => {
          const r = await graphqlQuery<DiscoveryProjectResult>(
            DISCOVERY_PROJECT_TITLE_QUERY,
            { id: uuid }
          );
          const title = r.project?.title;
          return title ? { title, uuid } : null;
        })
      );
      for (const entry of titleLookups) {
        if (entry) map.set(entry.title, entry.uuid);
      }
      return map;
    } catch {
      // Discovery is best-effort. If the network is down or the schema
      // changes, we just return an empty map; sub-queries will be
      // empty for unresolvable projects, which is the same behavior
      // we had before this discovery was added.
      return new Map<string, string>();
    }
  })();
  return realIdByTitlePromise;
}

/** Test-only: drop the module-level cache so listProjects re-discovers. */
export function _resetRealIdCacheForTests(): void {
  realIdByTitlePromise = null;
}

// =============================================================================
// REPOSITORY CLASS
// =============================================================================

export class GraphQLProjectsRepository {
  // =========================================================================
  // PROJECTS
  // =========================================================================

  async createProject(
    _accountId: string, // Not used in GraphQL backend
    data: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'progress'>
  ): Promise<Project> {
    const input = {
      title: data.title,
      description: data.description || '',
      status: data.status,
      health: data.health || null,
      priority: data.priority,
      progress: 0,
      startDate: data.startDate,
      dueAt: data.dueAt || null,
      budgetTotal: data.budgetTotal || null,
      risk: data.risk || 'ok',
      favorite: data.favorite || false,
      clientId: data.clientId || null,
      clientName: data.clientName || null,
      team: toTeamIds(data.team),
      tags: data.tags || [],
    };

    const result = await graphqlMutation<CreateProjectResult>(
      MUTATIONS.createProject,
      { input }
    );

    return this.mapProjectFromGraphQL(result.createProject);
  }

  async getProject(id: string): Promise<Project | null> {
    try {
      const result = await graphqlQuery<ProjectQueryResult>(
        QUERIES.project,
        { id }
      );

      if (!result.project) return null;
      return this.mapProjectFromGraphQL(result.project);
    } catch {
      return null;
    }
  }

  async listProjects(
    _accountId: string, // Not used in GraphQL backend
    filters?: {
      status?: ProjectStatus;
      limit?: number;
    }
  ): Promise<Project[]> {
    const result = await graphqlQuery<ProjectsQueryResult>(QUERIES.projects);

    // TODO(issue:backend-id-serialization): the live `database.teknology.eu`
    // backend currently returns `id: ""` for EVERY project row in the
    // `projects` query (and in `project(id: <uuid>)` too), even though
    // `createProject` correctly returns a real UUID. The mutation input
    // round-trips fine, so the data IS in the DB — only the read-side
    // serialization is broken. Without a client-side workaround the user
    // sees an empty /projects page even when projects exist for their org.
    //
    // The mapper mints a deterministic synthetic id `synth-<slug>` from
    // the title so URLs stay stable across reloads. To make sub-queries
    // (tasks, milestones, time entries) work, we additionally discover
    // the real uuid of each project by matching the title of
    // `project(id: <real>)` against the foreign keys on tasks. See
    // buildRealIdByTitle for the protocol. Once the backend ships a
    // real id on the read side, both workarounds are no-ops.
    const realIdsByTitle = await buildRealIdByTitle();
    const projects = (result.projects ?? []).map((p) =>
      this.mapProjectFromGraphQL(p, realIdsByTitle.get(p.title))
    );

    // Apply client-side filters
    if (filters?.status) {
      projects = projects.filter(p => p.status === filters.status);
    }

    if (filters?.limit) {
      projects = projects.slice(0, filters.limit);
    }

    // Sort by updatedAt descending
    projects.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

    return projects;
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    // First get the current project
    const current = await this.getProject(id);
    if (!current) {
      throw new Error(`Project not found: ${id}`);
    }

    const input = {
      title: updates.title ?? current.title,
      description: updates.description ?? current.description,
      status: updates.status ?? current.status,
      health: updates.health ?? current.health ?? null,
      priority: updates.priority ?? current.priority,
      progress: updates.progress ?? current.progress,
      startDate: updates.startDate ?? current.startDate,
      dueAt: updates.dueAt ?? current.dueAt ?? null,
      budgetTotal: updates.budgetTotal ?? current.budgetTotal ?? null,
      risk: updates.risk ?? current.risk,
      favorite: updates.favorite ?? current.favorite,
      clientId: updates.clientId ?? current.clientId ?? null,
      clientName: updates.clientName ?? current.clientName ?? null,
      team: toTeamIds(updates.team ?? current.team),
      tags: updates.tags ?? current.tags ?? [],
    };

    const result = await graphqlMutation<UpdateProjectResult>(
      MUTATIONS.updateProject,
      { id, input }
    );

    return this.mapProjectFromGraphQL(result.updateProject);
  }

  async deleteProject(id: string): Promise<void> {
    await graphqlMutation(MUTATIONS.deleteProject, { id });
  }

  // =========================================================================
  // TASKS
  // =========================================================================

  async createTask(
    _accountId: string, // Not used in GraphQL backend
    projectId: string,
    data: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'projectId' | 'loggedMinutes'>
  ): Promise<Task> {
    const input = {
      projectId,
      parentId: data.parentId ?? null,
      order: data.order ?? 0,
      title: data.title,
      description: data.description ?? null,
      status: data.status ?? 'todo',
      done: data.done ?? false,
      priority: data.priority ?? 'media',
      startAt: data.startAt ?? null,
      dueAt: data.dueAt ?? null,
      estimatedMinutes: data.estimatedMinutes ?? null,
      loggedMinutes: null,
      assignees: data.assignees ?? [],
      tags: data.tags ?? [],
    };

    const result = await graphqlMutation<CreateTaskResult>(
      MUTATIONS.createTask,
      { input }
    );

    return this.mapTaskFromGraphQL(result.createTask);
  }

  async getTask(id: string): Promise<Task | null> {
    try {
      const result = await graphqlQuery<TaskQueryResult>(
        QUERIES.task,
        { id }
      );

      if (!result.task) return null;
      return this.mapTaskFromGraphQL(result.task);
    } catch {
      return null;
    }
  }

  async listProjectTasks(projectId: string): Promise<Task[]> {
    const result = await graphqlQuery<TasksQueryResult>(
      QUERIES.tasks,
      { projectId }
    );

    return result.tasks
      .map(t => this.mapTaskFromGraphQL(t))
      .sort((a, b) => a.order - b.order);
  }

  async updateTask(id: string, updates: Partial<Task>): Promise<Task> {
    // First get the current task
    const current = await this.getTask(id);
    if (!current) {
      throw new Error(`Task not found: ${id}`);
    }

    const input = {
      projectId: current.projectId,
      parentId: updates.parentId ?? current.parentId ?? null,
      order: updates.order ?? current.order,
      title: updates.title ?? current.title,
      description: updates.description ?? current.description ?? null,
      status: updates.status ?? current.status,
      done: updates.done ?? current.done,
      priority: updates.priority ?? current.priority ?? 'media',
      startAt: updates.startAt ?? current.startAt ?? null,
      dueAt: updates.dueAt ?? current.dueAt ?? null,
      estimatedMinutes: updates.estimatedMinutes ?? current.estimatedMinutes ?? null,
      loggedMinutes: updates.loggedMinutes ?? current.loggedMinutes ?? null,
      assignees: updates.assignees ?? current.assignees ?? [],
      tags: updates.tags ?? current.tags ?? [],
    };

    const result = await graphqlMutation<UpdateTaskResult>(
      MUTATIONS.updateTask,
      { id, input }
    );

    return this.mapTaskFromGraphQL(result.updateTask);
  }

  async deleteTask(id: string): Promise<void> {
    await graphqlMutation(MUTATIONS.deleteTask, { id });
  }

  // =========================================================================
  // TIME ENTRIES
  // =========================================================================

  async createTimeEntry(
    _accountId: string, // Not used in GraphQL backend
    projectId: string,
    data: Omit<TimeEntry, 'id' | 'createdAt' | 'projectId'>
  ): Promise<TimeEntry> {
    const input = {
      projectId,
      taskId: data.taskId ?? null,
      userId: data.userId,
      userName: data.userName ?? null,
      minutes: data.minutes,
      costRate: data.costRate ?? 0,
      billableRate: data.billableRate ?? 0,
      billable: data.billable ?? false,
      description: data.description ?? null,
      at: data.at,
    };

    const result = await graphqlMutation<CreateTimeEntryResult>(
      MUTATIONS.createTimeEntry,
      { input }
    );

    return this.mapTimeEntryFromGraphQL(result.createTimeEntry);
  }

  async listProjectTimeEntries(projectId: string): Promise<TimeEntry[]> {
    const result = await graphqlQuery<TimeEntriesQueryResult>(
      QUERIES.timeEntries,
      { projectId }
    );

    return result.timeEntries
      .map(t => this.mapTimeEntryFromGraphQL(t))
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }

  // =========================================================================
  // MILESTONES
  // =========================================================================

  async createMilestone(
    _accountId: string, // Not used in GraphQL backend
    projectId: string,
    title: string,
    dueDate: string
  ): Promise<Milestone> {
    const input = {
      projectId,
      title,
      description: null,
      dueDate,
      completed: false,
    };

    const result = await graphqlMutation<CreateMilestoneResult>(
      MUTATIONS.createMilestone,
      { input }
    );

    return this.mapMilestoneFromGraphQL(result.createMilestone);
  }

  async listProjectMilestones(projectId: string): Promise<Milestone[]> {
    const result = await graphqlQuery<MilestonesQueryResult>(
      QUERIES.milestones,
      { projectId }
    );

    return result.milestones
      .map(m => this.mapMilestoneFromGraphQL(m))
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }

  async toggleMilestone(id: string): Promise<Milestone> {
    // Get current state
    const milestones = await graphqlQuery<MilestonesQueryResult>(QUERIES.milestones);
    const current = milestones.milestones.find(m => m.id === id);

    if (!current) {
      throw new Error(`Milestone not found: ${id}`);
    }

    const input = {
      projectId: current.projectId,
      title: current.title,
      description: current.description,
      dueDate: current.dueDate,
      completed: !current.completed,
    };

    const result = await graphqlMutation<UpdateMilestoneResult>(
      MUTATIONS.updateMilestone,
      { id, input }
    );

    return this.mapMilestoneFromGraphQL(result.updateMilestone);
  }

  // =========================================================================
  // MAPPERS (GraphQL -> Domain Models)
  // =========================================================================

  private mapProjectFromGraphQL(data: GraphQLProject, realId?: string): Project {
    // TODO(issue:backend-id-serialization): the backend still returns
    // `id: ""` on read paths. Until it is fixed, derive a deterministic
    // synthetic id from the title so the project is navigable. The slug
    // is stable across reloads (same title → same id), so deep links and
    // the `useProject` cache lookup both work. When the backend ships a
    // real id, this branch becomes a no-op (`data.id` is truthy) and
    // existing URLs keep working unchanged.
    //
    // `realId` is the real uuid we discovered by matching the title
    // against the foreign keys on tasks (see buildRealIdByTitle). It
    // is set only when the backend actually returned a non-empty id
    // for that title, and is what sub-queries (tasks, milestones,
    // time entries) prefer. When the backend read resolver is fixed,
    // `data.id` will be truthy and `realId` will be undefined again.
    const id = data.id || `synth-${slugifyTitle(data.title)}`;
    return {
      id,
      ...(realId ? { realId } : {}),
      title: data.title,
      description: data.description,
      status: data.status as ProjectStatus,
      health: (data.health as Project['health']) || 'da-iniziare',
      priority: data.priority as Project['priority'],
      progress: data.progress,
      startDate: data.startDate,
      dueAt: data.dueAt ?? undefined,
      budgetTotal: data.budgetTotal ?? undefined,
      budgetUsed: data.budgetUsed ?? undefined,
      risk: (data.risk as Project['risk']) || undefined,
      favorite: data.favorite,
      clientId: data.clientId ?? undefined,
      clientName: data.clientName ?? undefined,
      team: (data.team ?? []).map(userId => ({ userId, role: DEFAULT_TEAM_ROLE })),
      tags: data.tags || [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  }

  private mapTaskFromGraphQL(data: GraphQLTask): Task {
    return {
      id: data.id,
      projectId: data.projectId,
      parentId: data.parentId,
      order: data.order,
      title: data.title,
      description: data.description ?? undefined,
      status: data.status as TaskStatus,
      done: data.done,
      priority: (data.priority as Task['priority']) ?? undefined,
      startAt: data.startAt ?? undefined,
      dueAt: data.dueAt ?? undefined,
      estimatedMinutes: data.estimatedMinutes ?? undefined,
      loggedMinutes: data.loggedMinutes ?? undefined,
      assignees: data.assignees || [],
      tags: data.tags || [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  }

  private mapTimeEntryFromGraphQL(data: GraphQLTimeEntry): TimeEntry {
    return {
      id: data.id,
      projectId: data.projectId,
      taskId: data.taskId ?? undefined,
      userId: data.userId,
      userName: data.userName ?? undefined,
      minutes: data.minutes,
      costRate: data.costRate,
      billableRate: data.billableRate,
      billable: data.billable,
      description: data.description ?? undefined,
      at: data.at,
      createdAt: data.createdAt,
    };
  }

  private mapMilestoneFromGraphQL(data: GraphQLMilestone): Milestone {
    return {
      id: data.id,
      projectId: data.projectId,
      title: data.title,
      description: data.description ?? undefined,
      dueDate: data.dueDate,
      completed: data.completed,
      completedAt: data.completedAt ?? undefined,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  }
}

// Export singleton instance
export const graphqlProjectsRepository = new GraphQLProjectsRepository();
